/**
 * Trigger SSI Lead Override
 *
 * Calls the overrideSSIModule cloud function when a Lead clinician takes over
 * the Semi-Structured Interview module from the assigned SSI clinician
 * (SLaM / 3 Borough workflow).
 *
 * What the cloud function does:
 * 1. Validates the caller is the Lead and that leadOverride is enabled
 * 2. Snapshots the original SSI decisions, notes, outputs and approvals into
 *    patients/{id}/ssiOverrideArchive
 * 3. Writes dataModel.3_semiStructuredInterview.leadOverride so the Lead
 *    becomes the effective SSI owner for editing + AI paragraph review
 *
 * Importantly, clinicians.clinicianSSI and firstCompletedAt are NOT touched —
 * the patient report continues to attribute the SSI to the original clinician
 * with the original appointment date.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';

/**
 * Trigger Lead takeover of the SSI module.
 *
 * @param {string} patientId - The patient ID
 * @param {string} [reason] - Optional reason for the override (for audit)
 * @returns {Promise<{success: boolean, message: string, archiveId?: string, error?: Error}>}
 */
export const TriggerOverrideSSI = async (patientId, reason = null) => {
  console.log('TriggerOverrideSSI: Starting SSI override for patient', patientId);

  try {
    const overrideSSIModule = httpsCallable(functions, 'overrideSSIModule');

    const requestData = {
      patientId,
      reason: reason || 'Lead clinician took over SSI module ownership',
    };

    const result = await overrideSSIModule(requestData);

    console.log('SSI Override successful:', result.data);

    return {
      success: true,
      message: result.data.message,
      archiveId: result.data.archiveId,
    };
  } catch (error) {
    console.error('Error overriding SSI:', error);

    let errorMessage = 'An error occurred while taking over the SSI module.';

    if (error.code === 'functions/permission-denied') {
      errorMessage = 'Only the Lead clinician can take over the SSI module.';
    } else if (error.code === 'functions/failed-precondition') {
      // Surface the CF's specific precondition message when available
      // (e.g. SSI not completed, override already active, feature disabled).
      errorMessage = error.message || 'SSI cannot be overridden in its current state.';
    } else if (error.code === 'functions/not-found') {
      errorMessage = 'Patient not found.';
    } else if (error.code === 'functions/unauthenticated') {
      errorMessage = 'You must be logged in to take over the SSI module.';
    } else if (error.message) {
      errorMessage = error.message;
    }

    return {
      success: false,
      message: errorMessage,
      error,
    };
  }
};

export default TriggerOverrideSSI;
